import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../axios_Interceptor/api';
import { SERVER_ADDRESS } from '../server';
import { AdminActionButton } from './AdminPageHeader';

export type ClaimBankCashReceiptState = { issued: true; cancel_status: 'pending' | 'completed';
  items: { cancellation_seq: number; detail_seq: number; status: 'pending' | 'completed' }[] };
type Props = { orderSeq: number; detailSeqs?: number[]; receipt?: ClaimBankCashReceiptState | null;
  canManage?: boolean; refreshKey?: string | number; onChanged?: () => void; onBusy?: (busy: boolean) => void };
const validReceipt = (value: any): value is ClaimBankCashReceiptState | null => value === null || (value?.issued === true
  && ['pending', 'completed'].includes(value.cancel_status) && Array.isArray(value.items) && value.items.length > 0
  && value.items.every((item: any) => Number.isSafeInteger(item.cancellation_seq) && item.cancellation_seq > 0
    && Number.isSafeInteger(item.detail_seq) && item.detail_seq > 0 && ['pending', 'completed'].includes(item.status)));
const failure = (cause: any) => typeof cause?.response?.data?.message === 'string' ? cause.response.data.message
  : '현금영수증 취소 확인 기록을 저장하지 못했습니다. 다시 확인해주세요.';

export default function ClaimBankCashReceipt({ orderSeq, detailSeqs, receipt, canManage = true, refreshKey, onChanged, onBusy }: Props) {
  const [current, setCurrent] = useState<ClaimBankCashReceiptState | null>(receipt || null);
  const [checked, setChecked] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const generation = useRef(0), sending = useRef(false);
  const endpoint = `${SERVER_ADDRESS}orders/bank-cash-receipts/${orderSeq}/cancellation-confirmation`;
  const selection = detailSeqs?.join(',');
  useEffect(() => {
    const request = ++generation.current; setChecked(false); setError(''); setCurrent(receipt || null);
    if (receipt === undefined) void api.get(endpoint).then(({ data }) => {
      if (request !== generation.current) return;
      if (data?.order_seq !== orderSeq || !validReceipt(data.bank_cash_receipt)) throw new Error('Invalid receipt status');
      setCurrent(data.bank_cash_receipt);
    }).catch(() => { if (request === generation.current) setError('현금영수증 취소 상태를 불러오지 못했습니다. 처리 내역을 다시 불러와 주세요.'); });
    return () => { generation.current++; };
  }, [endpoint, orderSeq, receipt, refreshKey, selection]);
  const items = current?.items.filter(item => !detailSeqs || detailSeqs.includes(item.detail_seq)) || [];
  const pending = items.filter(item => item.status === 'pending');
  const confirm = async () => {
    if (!canManage || !checked || sending.current || !pending.length) return;
    const request = generation.current, ids = pending.map(item => item.cancellation_seq);
    sending.current = true; setBusy(true); onBusy?.(true); setError('');
    try {
      const { data } = await api.post(endpoint, { cancellation_seqs: ids, confirmed: true });
      if (request !== generation.current) return;
      if (data?.order_seq !== orderSeq || !validReceipt(data.bank_cash_receipt) || !data.bank_cash_receipt
        || ids.some(seq => !data.bank_cash_receipt.items.some((item: any) => item.cancellation_seq === seq && item.status === 'completed'))) {
        throw new Error('Invalid receipt confirmation');
      }
      setCurrent(data.bank_cash_receipt); setChecked(false); onChanged?.();
    } catch (cause) { if (request === generation.current) setError(failure(cause)); }
    finally { sending.current = false; setBusy(false); onBusy?.(false); }
  };
  if (!items.length && !error) return null;
  return <Section aria-label="현금영수증 취소 확인" aria-busy={busy} data-state={pending.length ? 'pending' : 'completed'}>
    {items.length > 0 && <><div className="receipt-heading">
      <strong>{pending.length ? '현금영수증 취소 대기' : '현금영수증 취소 완료'}</strong>
      <span className="receipt-count">대상 {items.length}건</span>
    </div>
      {!pending.length && <span className="receipt-record">외부 취소 확인 기록</span>}
      {pending.length > 0 && canManage && <><Confirmation><input type="checkbox" checked={checked} disabled={busy}
        onChange={event => setChecked(event.target.checked)} /><span>외부에서 현금영수증을 취소했습니다.</span></Confirmation>
        <ConfirmButton $primary type="button" disabled={busy || !checked} onClick={() => void confirm()}>
          {busy ? '확인 기록 저장 중…' : '현금영수증 취소 완료 확인'}
        </ConfirmButton></>}
    </>}
    {error && <p role="alert">{error}</p>}
  </Section>;
}
const Section = styled.section`
  box-sizing: border-box; display: grid; gap: var(--space-3); width: 100%; min-width: 0;
  padding: var(--space-4); border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
  background: var(--color-bg-surface); color: var(--color-text-primary);
  font-family: var(--font-family-base); font-size: var(--text-body); line-height: var(--leading-body);
  .receipt-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--space-2); }
  .receipt-heading strong { font-size: var(--text-body); font-weight: var(--font-weight-semibold); word-break: keep-all; }
  .receipt-count, .receipt-record { font-size: var(--text-caption); color: var(--color-text-secondary); }
  .receipt-count { white-space: nowrap; font-variant-numeric: tabular-nums; }
  &[data-state="completed"] .receipt-heading strong { color: var(--color-success); }
  && p { margin: 0; color: var(--color-text-danger); font-size: var(--text-caption); overflow-wrap: anywhere; }
`;
const Confirmation = styled.label`
  && { display: flex; flex-direction: row; align-items: center; justify-content: flex-start; gap: var(--space-3);
    width: 100%; min-width: 0; min-height: var(--touch-target); margin: 0; text-align: left;
    color: var(--color-text-primary); font-size: var(--text-body); font-weight: var(--font-weight-regular);
    line-height: var(--leading-body); cursor: pointer; }
  && input[type="checkbox"] { flex: 0 0 var(--space-5); width: var(--space-5); height: var(--space-5);
    min-width: var(--space-5); min-height: var(--space-5); margin: 0; padding: 0; accent-color: var(--color-brand); cursor: pointer; }
  && input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  && input:disabled { cursor: not-allowed; }
  && span { flex: 1; min-width: 0; word-break: keep-all; overflow-wrap: anywhere; }
`;
const ConfirmButton = styled(AdminActionButton)`
  && { width: 100%; min-width: 0; min-height: var(--control-lg); padding: var(--space-2) var(--space-3);
    white-space: normal; line-height: var(--leading-body); }
  &&:disabled { background: var(--color-bg-muted); border-color: var(--color-border-default); color: var(--color-text-disabled); }
`;
